import { FC } from 'react';

import { useDropzone } from 'react-dropzone';
import type { DropEvent, FileRejection } from 'react-dropzone';
import { Button } from 'ui/components/common';
import { Textarea } from 'ui/components/form';
import UploadListItem from 'ui/partials/Composer/UploadListItem';
import c, { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import { Attachment } from '../../NotesComment/Comment';

interface PROPS {
  disabled?: boolean;
  onSave: () => void;
  onCancel: () => void;
  name: string;
  placeholder: string;
  onChange: (text: string) => void;
  onDrop: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => void;
  onDropRejected: (fileRejections: FileRejection[]) => void;
  value?: string;
  attachments: Attachment[];
  onRemoveAttachment: (name: string) => void;
  isLoading: boolean;
}

const NoteCommentForm: FC<PROPS> = ({
  disabled,
  isLoading,
  onSave,
  onCancel,
  name,
  placeholder,
  onChange,
  value,
  onDrop,
  onDropRejected,
  attachments,
  onRemoveAttachment,
}) => {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxSize: c.MAX_UPLOAD_SIZE,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onDropRejected,
  });
  return (
    <div>
      <div {...getRootProps()} className="relative flex gap-2 flex-col">
        <input {...getInputProps()} />
        <Textarea
          richText
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          value={value}
          onClickAttachButton={open}
          className="rounded-[6px] border-[#DCECF7]"
        >
          <div
            className={cn(
              'space-y-[6px] rounded-b-[6px] bg-fill-tertiary px-[14px]',
              'max-h-[200px]',
              'w-full',
              'overflow-y-auto',
            )}
          >
            {attachments?.map((file) => (
              <UploadListItem
                key={file.name}
                showSize
                file={{
                  ...file,
                  fileName: file.name,
                  fileType: file.type,
                  fileSize: file.size,
                }}
                size={SIZE.Large}
                onRemove={() => onRemoveAttachment(file.name)}
                isUploaded={false}
              />
            ))}
          </div>
        </Textarea>
        {isDragActive && (
          <div
            className={cn(
              'absolute left-0 inset-y-0',
              'bg-fill-tertiary',
              'bg-opacity-90',
              'w-full h-full',
              'grid place-items-center',
              'border-2 border-dashed border-secondary',
            )}
          >
            <div className="text-lg">Drop Files Here</div>
          </div>
        )}
      </div>
      <div className="py-[16px] flex gap-x-3">
        <Button
          disabled={disabled || isLoading}
          onClick={onSave}
          intent="secondary"
          label="SAVE"
        />
        <Button onClick={onCancel} intent="tertiary" size="sm" label="CANCEL" />
      </div>
    </div>
  );
};

export default NoteCommentForm;
